/* eslint-disable react-hooks/exhaustive-deps */

// Generic Packages & React
import { useEffect, useState, useMemo, useRef } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import _ from 'lodash';
import { rateLocationMatches } from './rateLocationMatches.js';
import stringSimilarity from 'string-similarity';

// MUI Imports
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import AppBar from '@mui/material/AppBar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Pagination from '@mui/material/Pagination';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import { green, blue, red, yellow, cyan } from '@mui/material/colors';
import { useTheme, alpha } from '@mui/material/styles';

// FontAwesome Imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash, faUpload, faDiagramVenn, faCoins, faMagnifyingGlass, faCheck, faXmark } from '@fortawesome/pro-solid-svg-icons';

// Local Imports
import TransportationMethod from '../../TransportationMethod.js';
import Header from '../../Header.js';
import RatesDetails from '../../RatesDetails.js';
import { RateCard } from '../../RateCard.js';
import ContractBuilder from '../contractbuilder/ContractBuilder.js';
import { Filters } from './Rates.filters.js';
import { getProductInOffer } from '../utils/opportunity.js';
import { getOverlappingRates, getRatesFromServer } from '../utils/rates.js';
import { createAllRateProducts } from '../utils/product.js';

const BACKGROUNDS = [green[500], blue[500], red[500], yellow[500], cyan[500]];

function Rates(props) {
    const { org, accesstoken, exchangeRates } = useOutletContext();
    const { type, mode } = props;
    const { rateId } = useParams();
    const [rates, setRates] = useState(undefined);
    const [base, setBase] = useState(null);
    const [progress, setProgress] = useState({ count: 0, total: 0 });

    const products = useMemo(() => {
        const pairs = ['air', 'sea', 'land'].map(transportationMethod => ([transportationMethod, createAllRateProducts({ transportationMethod })]));
        return _.fromPairs(pairs);
    }, []);

    useEffect(() => {
        setRates(undefined);
        setBase(null);
        (async () => {

            const data = await getRatesFromServer({ mode, accesstoken, organizationId: org.id, onProgress: ({ count, total }) => {
                setProgress({ count, total });
            } });
            const _rates = matchProducts({ rates: _.filter(data, r => r.id !== org.id && r.product), products });
            setRates(_rates.filter(r => !r.deleted));
            setBase(_.fromPairs(_.cloneDeep(_rates.filter(r => !r.deleted)).map(r => ([ r.id, r ]))));

            window[`all${mode}`] = data;
            localStorage.setItem(`${org.alias}-${mode}`, new Date().getTime());
        })();
    }, [mode]);

    function onUpdate({ rate }) {
        if ((rates || []).find(r => r.id === rate.id)) {
            setRates(rates.map(r => r.id === rate.id ? rate : r));
        } else {
            setRates([...(rates || []), rate]);
        }
    }

    if (!rates) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '48px', alignItems: 'center', flexDirection: 'column' }}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <CircularProgress size={80} variant={progress.total > 0 ? 'determinate' : 'indeterminate'} value={progress.total > 0 ? (progress.count/progress.total * 100) : 0} />
                    {progress.total > 0 && (
                        <Box
                            sx={{
                                top: 0,
                                left: 0,
                                bottom: 0,
                                right: 0,
                                position: 'absolute',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Typography variant="caption" component="div" color="text.secondary">
                                {`${Math.round(Math.min(progress.count/progress.total * 100, 100))}%`}
                            </Typography>
                        </Box>
                    )}
                </Box>
                {(!localStorage.hasOwnProperty(`${org.alias}-rates`)) && (
                    <Typography variant='body2' sx={{ marginTop: '24px' }}>
                        Loading rates for the first time. This may take a few minutes. Don&apos;t worry, next times will be faster.
                    </Typography>
                )}
            </Box>
        );
    }

    return (
        <>
            <div style={{ display: !!type ? 'none' : 'block' }}>
                <RatesMain rates={rates} setRates={setRates} base={base} setBase={setBase} mode={mode} products={products} />
            </div>
            {type === 'overlapping' && (
                <RatesOverlapping rates={rates} rateId={rateId} onUpdate={onUpdate} mode={mode} exchangeRates={exchangeRates} />
            )}
        </>
    );
}

function RatesMain(props) {
    const { org, accesstoken, me, seaPorts, exchangeRates, unlocode, suppliers } = useOutletContext();
    const { rates, setRates, base, setBase, mode, products, noHeader, forceTransportationMethod, 
        forceSaving, forceSavingSuccess, forceSavingError, onDeleteOffer } = props;
    const [filters, setFilters] = useState({});
    const [forceFilter, setForceFilter] = useState([]);
    const [transportationMethod, setTransportationMethod] = useState(forceTransportationMethod || '');
    const [saving, setSaving] = useState([]);
    const [savingSuccess, setSavingSuccess] = useState([]);
    const [savingError, setSavingError] = useState([]);
    // rate id -> why the save was rejected, so the row can say what went wrong
    // instead of just flashing a red X.
    const [savingErrorMessages, setSavingErrorMessages] = useState({});
    // Always-current view of `rates`, so a save triggered from a focused cell reads
    // the value that cell just committed rather than the one captured at render.
    const ratesRef = useRef(rates);
    // Bulk supplier change. Rates arrive from an import stamped with whatever supplier the
    // import carried - a CargoWise file landing under "CMA CGM" that belongs to someone
    // else - and there was no way to correct that short of re-importing.
    const [ bulkSupplier, setBulkSupplier ] = useState(null);
    const [ bulkConfirm, setBulkConfirm ] = useState(false);
    const [ bulkProgress, setBulkProgress ] = useState(null);
    const [ bulkError, setBulkError ] = useState(null);
    const [ page, setPage ] = useState(1);
    const [ pageSize, setPageSize ] = useState(10);
    const [ deleteDialog, setDeleteDialog ] = useState({ open: false, offerId: null });
    const navigate = useNavigate();
    const theme = useTheme();

    /*
    window.fixRates = async () => {
        const r = rates.filter(r => _.some(r.offer.sections, s => _.some(s.offers, o => _.some(o.fields, f => f.type === 'flat' && !f.values.flat))));
        r.forEach(r => {
            const o = r.offer;
            o.sections.forEach(s => {
                s.offers.forEach(o => {
                    o.fields.forEach(f => {
                        if (f.type === 'flat' && !f.values.flat) {
                            f.values.flat = f.values[_.keys(f.values).filter(k => k.length > 10)[0]];
                        }
                    });
                });
            });
        });

        for (let i = 0 ; i < r.length ; i++) {
            const rate = r[i];
            console.log('Saving rate', rate.id);
            await axios.put(`/api/${mode}/${rate.id}?accesstoken=${accesstoken}&organizationId=${org.id}`, { [mode === 'rates' ? 'rate' : mode === 'tariff' ? 'tariff' : 'bug']: rate });
        }
    }
        */

    useEffect(() => { ratesRef.current = rates; }, [rates]);

    const dirty = useMemo(() => {
        if ((!rates) || (!base)) return [];
        const a = rates.filter(r => {
            const b = base[r.id];
            if (!b) return true;
            if (mode === 'tariff') {
                const omits = ['modified', 'supplier', 'deleted'];
                if (!r.source && !b.source) omits.push('source');
                if (!r.destination && !b.destination) omits.push('destination');
                return !_.isEqual(_.omit(b, omits), _.omit(r, omits));
            } else {
                if (b.modified === r.modified) return false;
                return !_.isEqual(_.omit(b, ['modified']), _.omit(r, ['modified']));
            }
        }).map(r => r.id);
        return a;
    }, [ base, rates ]);

    useEffect(() => {
        setPage(1);
        setForceFilter([]);
    }, [filters, transportationMethod]);

    useEffect(() => {
        if (forceSaving && forceSavingSuccess && forceSavingError) {
            setSaving(forceSaving);
            setSavingSuccess(forceSavingSuccess);
            setSavingError(forceSavingError);
        }
    }, [forceSaving, forceSavingSuccess, forceSavingError]);

    const filteredRates = useMemo(() => {
        if (!rates) return null;
        if (!transportationMethod) return null;
        let filtered = rates.filter(r => r.transportationMethod === transportationMethod);
        if (!_.isEmpty(filters.products)) filtered = filtered.filter(r => {
            if (!r.product) return false;
            if (filters.products.includes(r.product.type)) return true;
            if (r.product.type === 'LCL' && filters.products.includes('LCL')) return true;
            return false;
        });
        if (!_.isEmpty(filters.suppliers)) filtered = filtered.filter(r => filters.suppliers.includes(r.supplier?.organization));
        if (!_.isEmpty(filters.sources)) filtered = filtered.filter(r => _.some(filters.sources, source => rateLocationMatches({ location: r.source, filter: source, incEverywhere: filters.incEverywhere })));
        if (!_.isEmpty(filters.destinations)) filtered = filtered.filter(r => _.some(filters.destinations, destination => rateLocationMatches({ location: r.destination, filter: destination, incEverywhere: filters.incEverywhere })));

        if (filters.minValidFrom) filtered = filtered.filter(r => new Date(r.offer.validFrom).getTime() >= filters.minValidFrom);
        if (filters.maxValidFrom) filtered = filtered.filter(r => new Date(r.offer.validFrom).getTime() <= filters.maxValidFrom);
        if (filters.minValidUntil) filtered = filtered.filter(r => new Date(r.offer.validUntil).getTime() >= filters.minValidUntil);
        if (filters.maxValidUntil) filtered = filtered.filter(r => new Date(r.offer.validUntil).getTime() <= filters.maxValidUntil);

        if (filters.aiFunction) {
            const func = new Function('rates', 'getCostValue', filters.aiFunction);
            filtered = func(filtered, getCostValue);
        }
        forceFilter?.forEach(id => {
            const index = filtered.findIndex(r => r.id === id);
            if (index === -1) {
                filtered.push(rates.find(r => r.id === id && r.transportationMethod === transportationMethod));
                filtered = _.compact(filtered);
            }
        });
        return filtered;
    }, [rates, transportationMethod, filters]);

    // Supplier options: everyone already on these rates, plus the org's supplier directory,
    // so a rate can be moved to a supplier that has no rates yet. Existing names only - the
    // point is to correct a wrong one, and free text would just add near-duplicates.
    const supplierOptions = useMemo(() => _.sortBy(_.uniq(_.compact([
        ...(rates || []).map(r => r.supplier?.organization),
        ...(suppliers || []).map(sup => sup.name || sup.organization),
    ]))), [rates, suppliers]);

    // Applies to every rate the filters currently match, which is what makes "a few" or
    // "all of them" the same action - narrow with the filters, then apply.
    async function onBulkChangeSupplier() {
        const target = filteredRates || [];
        setBulkConfirm(false);
        setBulkError(null);
        setBulkProgress({ done: 0, total: target.length });

        // The server changes them in parallel; a few hundred per request keeps the progress moving
        // and each request short.
        const updated = [];
        for (const batch of _.chunk(target, 250)) {
            try {
                const { data } = await axios.post(`/api/rates/supplier?accesstoken=${accesstoken}&organizationId=${org.id}`,
                    { ids: batch.map(r => r.id), supplier: bulkSupplier });
                const changed = new Set(data.updated);
                batch.filter(r => changed.has(r.id)).forEach(rate => updated.push({
                    ...rate,
                    supplier: { ...(rate.supplier || {}), organization: bulkSupplier },
                    modified: data.modified,
                }));
            } catch (e) {
                // Stop at the first failure; the rates changed so far are kept and shown.
                setBulkError(`${e?.response?.data?.detail || e.message} — stopped after ${updated.length} of ${target.length}.`);
                break;
            }
            setBulkProgress({ done: updated.length, total: target.length });
        }

        if (updated.length > 0) {
            const byId = _.keyBy(updated, 'id');
            setRates((rates || []).map(r => byId[r.id] || r));
        }
        setBulkProgress(null);
        setBulkSupplier(null);
    }

    // Saving from a focused cell blurs it in the same tick, so the offer handed to
    // the click handler is the pre-edit one. Wait for that commit to land, then take
    // the row's current value from state instead of the stale copy.
    async function onSaveOffer(offer) {
        await new Promise(res => setTimeout(res, 0));
        const rate = ratesRef.current?.find(r => r.id === offer.id)
            || convertOfferToRate({ offer, products, transportationMethod });
        return onSave({ rate, updateSaving: true });
    }

    async function onSave({ rate, updateSaving }) {
        if (updateSaving) setSaving(s => ([...s, rate.id]));
        // Clear the previous failure for this row — this attempt replaces it.
        setSavingError(s => _.without(s, rate.id));
        setSavingErrorMessages(m => _.omit(m, rate.id));

        try {
            await axios.put(`/api/${mode}/${rate.id}?accesstoken=${accesstoken}&organizationId=${org.id}`, { [mode === 'rates' ? 'rate' : mode === 'tariff' ? 'tariff' : 'bug']: rate });
            if (updateSaving) setSaving(s => _.without(s, rate.id));
            setBase(b => ({ ...b, [rate.id]: rate }));
            if (updateSaving) {
                setSavingSuccess(s => ([...s, rate.id]));
                await new Promise(res => setTimeout(res, 2000));
                setSavingSuccess(s => _.without(s, rate.id));
            }
        } catch(e) {
            if (updateSaving) {
                setSaving(s => _.without(s, rate.id));
                setSavingError(s => ([...s, rate.id]));
                // Keep the failure on screen until the next attempt — a two-second
                // flash gave the user no chance to read why the save was rejected.
                setSavingErrorMessages(m => ({ ...m, [rate.id]: saveErrorMessage(e) }));
            } else {
                throw e;
            }
        }
    }

    async function onAdminDelete(_rates) {
        await axios.post(`/api/${mode}/admin-delete?accesstoken=${accesstoken}&organizationId=${org.id}`, { rates: _rates });
        setRates(rates.filter(r => !_rates.includes(r.id)));
    }

    async function _onDeleteOffer({ offerId }) {
        setDeleteDialog({ open: true, offerId });
    }

    async function handleDeleteConfirm() {
        const { offerId } = deleteDialog;
        try {
            await axios.delete(`/api/${mode.split('-')[0]}/${offerId}?accesstoken=${accesstoken}&organizationId=${org.id}`);
            if (mode !== 'rates-import') {
                setRates(rates.map(r => r.id === offerId ? { ...r, deleted: true } : r));
            } else if (onDeleteOffer) {
                onDeleteOffer({ offerId });
            }
        } finally {
            setDeleteDialog({ open: false, offerId: null });
        }
    }

    async function handleDeleteCancel() {
        setDeleteDialog({ open: false, offerId: null });
    }

    async function onSaveAll() {
        setSaving(s => [...s, ...dirty, 'all']);

        const result = (await Promise.allSettled(dirty.map(id => {
            const rate = rates.find(r => r.id === id);
            return onSave({ rate, updateSaving: false });
        }))).map((r,i) => ({ status: r.status, value: r.value, reason: r.reason, rate: dirty[i] }));
        
        // `dirty` is a list of ids, so `r.rate` is already the id — reading `.id`
        // off it produced undefined and no row ever showed its result.
        const success = result.filter(r => r.status === 'fulfilled').map(r => r.rate);
        const failed = result.filter(r => r.status === 'rejected');
        const error = failed.map(r => r.rate);

        setSaving(s => _.without(s, ...dirty, 'all'));
        setSavingSuccess(s => _.compact([...s, ...success, _.isEmpty(error) ? 'all' : null]));
        setSavingError(s => _.compact([...s, ...error, _.isEmpty(error) ? null : 'all']));
        setSavingErrorMessages(m => ({ ...m, ..._.fromPairs(failed.map(r => ([ r.rate, saveErrorMessage(r.reason) ]))) }));
        await new Promise(res => setTimeout(res, 2000));
        setSavingSuccess(s => _.without(s, ...success, 'all'));
        // Failures stay put (with their reason) until the row is saved again.
        setSavingError(s => _.without(s, 'all'));
    }

    return (
        <Box sx={{ background: alpha(theme.palette.text.primary, 0.01), paddingBottom: '30px' }}>
            {!noHeader && (
                <Header
                    icon={faCoins}
                    title={mode === 'rates' ? 'Supplier Rates' : 'Company Tariff'}
                    tabs={mode === 'rates' ? _.compact([{
                        icon: faMagnifyingGlass,
                        title: 'Search',
                        action: () => navigate('../rates'),
                        selected: true,
                    }, {
                        icon: faUpload,
                        title: 'Import',
                        action: () => navigate('../rates/import')
                    }, me.email !== 'admin@monada.ai' ? null : {
                        icon: faUpload,
                        title: 'Upload',
                        action: () => navigate('../rates/upload')
                    }, {
                        icon: faDiagramVenn,
                        title: 'Overlapping',
                        action: () => navigate('../rates/overlapping')
                    }]) : null}
                />
            )}

            {(!_.isEmpty(dirty) || savingSuccess.includes('all') || savingError.includes('all')) && (
                <Box sx={{ position: 'sticky', top: '0px', zIndex: 1000, background: alpha(theme.palette.text.primary, 0.01), padding: '6px 0px' }}>
                    <Alert severity='warning' sx={{ margin: '12px'}}
                        action={
                            <Button
                                size='small'
                                variant='contained'
                                onClick={() => onSaveAll()}
                            >
                                {!_.isEmpty(saving) ? <div style={{color: 'white'}}><CircularProgress size={18} color='inherit' /></div> : savingSuccess.includes('all') ? <FontAwesomeIcon icon={faCheck} /> : savingError.includes('all') ? <FontAwesomeIcon icon={faXmark} /> : <>Save All</>}
                            </Button>
                        }
                    >
                        <AlertTitle>Unsaved Changes</AlertTitle>
                        You have {dirty.length} unsaved changes. Don&apos;t forget to save them.
                        {!_.isEmpty(savingErrorMessages) && (
                            <Box sx={{ mt: 1 }}>
                                <b>{_.keys(savingErrorMessages).length} could not be saved:</b>
                                <ul style={{ margin: '4px 0px 0px 0px', paddingLeft: '20px' }}>
                                    {_.uniq(_.values(savingErrorMessages)).map(msg => (
                                        <li key={msg}>{msg}</li>
                                    ))}
                                </ul>
                            </Box>
                        )}
                    </Alert>
                </Box>
            )}

            {!forceTransportationMethod && (
                <div style={{ padding: '12px', paddingTop: '16px' }}>
                    <TransportationMethod 
                        transportationMethod={transportationMethod}
                        setTransportationMethod={(v) => {
                            setTransportationMethod(v);
                        }}
                    />
                </div>
            )}

            {transportationMethod && (
                <Box sx={{ p: '0px 12px' }} >
                    <Filters
                        transportationMethod={transportationMethod} 
                        seaPorts={seaPorts}
                        suppliers={mode === 'tariff' ? null : rates ? _.uniq(rates.map(r => r.supplier?.organization)) : []}
                        filters={filters}
                        onFilter={(params) => setFilters(params)}
                        minValidFrom={rates ? Math.floor(_.min(rates.map(r => new Date(r.offer.validFrom).getTime())) / DAY ) * DAY : 0}
                        maxValidFrom={rates ? Math.ceil(_.max(rates.map(r => new Date(r.offer.validFrom).getTime())) / DAY ) * DAY : 100}
                        minValidUntil={rates ? Math.floor(_.min(rates.map(r => new Date(r.offer.validUntil).getTime())) / DAY ) * DAY : 0}
                        maxValidUntil={rates ? Math.ceil(_.max(rates.map(r => new Date(r.offer.validUntil).getTime())) / DAY ) * DAY : 100}
                        accesstoken={accesstoken}
                        org={org}
                        unlocode={unlocode}
                    />
                </Box>
            )}

            {/* Change the supplier on a whole batch at once. Rates take their supplier from
                the import that brought them in, so a file landing under the wrong name had
                no fix short of importing it again. Narrow with the filters above, then
                apply - that covers a handful or the lot without needing per-row ticks. */}
            {mode === 'rates' && transportationMethod && filteredRates && filteredRates.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', padding: '0px 12px 12px 12px' }}>
                    <Autocomplete
                        size='small'
                        sx={{ width: 280 }}
                        options={supplierOptions}
                        value={bulkSupplier}
                        onChange={(e, v) => setBulkSupplier(v)}
                        disabled={!!bulkProgress}
                        renderInput={(params) => <TextField {...params} label='Change supplier to' />}
                    />
                    <Button
                        variant='outlined'
                        color='primary'
                        disabled={!bulkSupplier || !!bulkProgress}
                        onClick={() => setBulkConfirm(true)}
                        sx={{ textTransform: 'none' }}
                    >
                        Apply to {filteredRates.length} {filteredRates.length === 1 ? 'rate' : 'rates'}
                    </Button>
                    {bulkProgress && (
                        <Typography variant='caption'>
                            Updating {bulkProgress.done} of {bulkProgress.total}…
                        </Typography>
                    )}
                    {bulkError && (
                        <Alert severity='error' sx={{ py: 0 }}>{bulkError}</Alert>
                    )}
                </Box>
            )}

            <Dialog open={bulkConfirm} onClose={() => setBulkConfirm(false)}>
                <DialogTitle>Change supplier</DialogTitle>
                <DialogContent>
                    <Typography variant='body2'>
                        Set the supplier to <b>{bulkSupplier}</b> on <b>{(filteredRates || []).length}</b>{' '}
                        {(filteredRates || []).length === 1 ? 'rate' : 'rates'} currently matching the filters?
                    </Typography>
                    <Typography variant='caption' sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                        This saves straight away and cannot be undone in bulk.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBulkConfirm(false)}>Cancel</Button>
                    <Button variant='contained' onClick={onBulkChangeSupplier}>Change supplier</Button>
                </DialogActions>
            </Dialog>

            {_.isNull(rates) && (
                <Box fullWidth sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress />
                </Box>
            )}

            {filteredRates && filteredRates.length === 0 && (
                <Alert severity='info' sx={{ margin: '12px' }}>
                    No rates matching the filters found. Please try again later.
                </Alert>
            )}

            {filteredRates && (
                <Box sx={{ padding: '0px 12px' }}>
                    <Box sx={{ paddingBottom: '12px', display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                            <Pagination page={page} onChange={(e, v) => setPage(v)} count={Math.ceil(filteredRates.length / pageSize)} variant='outlined' color='primary' />
                            <Typography variant='caption' sx={{ paddingLeft: '12px' }}>
                                Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, filteredRates.length)} of {filteredRates.length} rates.
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 0, display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                            <Typography variant='caption' sx={{ paddingLeft: '24px' }}>
                                Page size:
                            </Typography>
                            <FormControl sx={{ ml: 1, width: 45 }} size='small' variant='standard'>
                                <Select value={pageSize} onChange={e => setPageSize(e.target.value)}>
                                    {[10, 25, 50, 100, 300, 500].map(v => (
                                        <MenuItem key={v} value={v}>{v}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                    <ContractBuilder
                        key={`${page}-${filteredRates.length}-${transportationMethod}`}
                        mode={mode}
                        org={org}
                        routes={[]}
                        transportationMethod={transportationMethod}
                        offers={filteredRates.slice((page - 1) * pageSize, page * pageSize).map(convertRateToOffer)}
                        products={products[transportationMethod]}
                        exchangeRates={exchangeRates}
                        setOffers={offers => {
                            const _offers = (_.isFunction(offers) ? offers(filteredRates.slice((page - 1) * pageSize, page * pageSize).map(o => convertRateToOffer(o))) : offers).map(o => convertOfferToRate({ offer: o, products, transportationMethod }));

                            _offers.forEach(o => o.modified = new Date().getTime());

                            const newOffers = _offers.filter(o => !rates.find(r => r.id === o.id));
                            const index = rates.findIndex(r => r.id === filteredRates[(page - 1) * page]?.id);

                            const updateRates = rates.map(r => _offers.find(u => u.id === r.id) || r);
                            setRates([
                                ...updateRates.slice(0, index === -1 ? 0 : index),
                                ...newOffers,
                                ...updateRates.slice(index === -1 ? 0 : index)
                            ]);
                            setForceFilter(newOffers.map(o => o.id));
                        }}
                        dirty={dirty}
                        onSave={o => onSaveOffer(o)}
                        onAdminDelete={onAdminDelete}
                        onDeleteOffer={_onDeleteOffer}
                        saving={saving}
                        savingSuccess={savingSuccess}
                        savingError={savingError}
                        savingErrorMessages={savingErrorMessages}
                        size='small'
                        seaPorts={seaPorts}
                        unlocode={unlocode}
                        me={me}
                    />
                    <Box sx={{ paddingTop: '12px', display: 'flex', alignItems: 'center' }}>
                        <Pagination page={page} onChange={(e, v) => setPage(v)} count={Math.ceil(filteredRates.length / pageSize)} variant='outlined' color='primary' />
                        <Typography variant='caption' sx={{ paddingLeft: '12px' }}>
                            Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, filteredRates.length)} of {filteredRates.length} rates.
                        </Typography>
                    </Box>
                </Box>
            )}
        
            <Dialog
                open={deleteDialog.open}
                onClose={handleDeleteCancel}
            >
                <DialogTitle>Delete {mode === 'rates' ? 'Rate' : 'Tariff'}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this {mode === 'rates' ? 'rate' : 'tariff'}?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error">Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

// The API rejects a rate with a yup path, e.g.
//   "body.rate.source.countryCode is a required field"
// Strip the wrapper and name the field the way the UI labels it, so the row can
// show a reason rather than an unexplained failure.
const SAVE_ERROR_FIELD_LABELS = {
    supplier: 'Supplier',
    source: 'Origin',
    destination: 'Destination',
    product: 'Product',
    offer: 'Rate details',
    transportationMethod: 'Transport mode',
    type: 'Rate type',
};

function saveErrorMessage(e) {
    const detail = e?.response?.data?.detail;
    if (!detail) return e?.message || 'Could not save this rate.';

    // This page saves both rates and tariffs, so the wrapper is body.rate or body.tariff.
    const match = detail.match(/^body\.(rate|tariff)\.?([^\s]*)\s+(.*)$/);
    if (!match) return detail;

    const [ , kind, path, rest ] = match;
    if (!path) return `This ${kind} ${rest}`;

    const [ head, ...tail ] = path.split('.');
    const label = SAVE_ERROR_FIELD_LABELS[head] || head;
    return _.isEmpty(tail) ? `${label} ${rest}` : `${label} — ${tail.join('.')} ${rest}`;
}

function convertRateToOffer(rate) {
    return {
        ...rate.offer,
        ...(rate.deleted ? { deleted: rate.deleted } : {}),
        routes: [{
            source: rate.source,
            destination: rate.destination,
        }],
        id: rate.id,
        supplier: rate.supplier,
        type: rate.type,
        // Carry the rate's product so getProductInOffer can resolve it even when the offer's
        // charges are all `flat` fields (FCL/per-shipment) that don't reference the product id.
        product: rate.product,
    };
}

function convertOfferToRate({ offer, products, transportationMethod }) {
    const product = getProductInOffer({ offer, products: products[transportationMethod] });
    return {
        id: offer.id,
        source: offer.routes[0].source,
        destination: offer.routes[0].destination,
        offer: _.omit(offer, ['id', 'routes', 'supplier', 'type', 'deleted', 'product']),
        supplier: offer.supplier,
        product,
        transportationMethod,
        ...(offer.deleted ? { deleted: offer.deleted } : {}),
        ...(offer.type ? { type: offer.type.toLowerCase() } : {}),
    };
}

function RatesOverlapping(props) {
    const { mode, exchangeRates } = props;
    const { me, org, accesstoken } = useOutletContext();
    const [ rates, setRates ] = useState(null);
    const [ openRate, setOpenRate ] = useState(null);
    const [ deleteCandidate, setDeleteCandidate ] = useState(null); 
    const navigate = useNavigate();
        const theme = useTheme();

        useEffect(() => {
            (async () => {
                const rates = await getRatesFromServer({ mode, accesstoken, organizationId: org.id, since: 0 });
                const data = getOverlappingRates({ rates });
                setRates(data);
            })()
        }, []);

        async function onDeleteConfirm() {
            await axios.delete(`/api/${mode}/${deleteCandidate}?accesstoken=${accesstoken}&organizationId=${org.id}`);
            const candidate = deleteCandidate;
            setDeleteCandidate(null);
            setRates(rates.map(groups => groups.filter(r => r.id !== candidate)));
        }

        return (
            <Box sx={{ background: alpha(theme.palette.text.primary, 0.01) }}>
                <Header
                    icon={faCoins}
                    title={mode === 'rates' ? 'Supplier Rates' : 'Company Tariff'}
                    tabs={mode === 'rates' ? _.compact([{
                        icon: faMagnifyingGlass,
                        title: 'Search',
                        action: () => navigate('../rates')
                    }, {
                        icon: faUpload,
                        title: 'Import',
                        action: () => navigate('../rates/import')
                    }, me.email !== 'admin@monada.ai' ? null : {
                        icon: faUpload,
                        title: 'Upload',
                        action: () => navigate('../rates/upload')
                    }, {
                        icon: faDiagramVenn,
                        title: 'Overlapping',
                        action: () => navigate('../rates/overlapping'),
                        selected: true,
                    }]) : null}
                />

                {!rates && (
                    <Box fullWidth sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CircularProgress />
                    </Box>
                )}
                <Box sx={{ p: 3 }}>
                    {_(rates || []).sortBy(r => `${r[0].supplier.organization.toUpperCase()} - ${r[0].source.text} - ${r[0].destination.text} - ${r[0].product || 'Route'} - ${r[0].dangerous ? 'yes' : 'no'}`).map(r => {
                        if (r.length === 1) return null;
                        const min = _.min(r.map(r2 => r2.offer.validFrom ? new Date(r2.offer.validFrom).getTime() / 1000 / 60 / 60 : 0));
                        const max = _.max(r.map(r2 => r2.offer.validUntil ? new Date(r2.offer.validUntil).getTime() / 1000 / 60 / 60 : 1139566));
                        return (
                            <div key={`${r[0].supplier.organization} - ${r[0].source.text} - ${r[0].destination.text} - ${r[0].product.type || 'Route'} - ${r[0].dangerous ? 'yes' : 'no'}`}>
                                <h5>
                                    {r[0].supplier.organization} - {r[0].source.text} - {r[0].destination.text} - {r[0].product.type || 'Route'}{r[0].dangerous ? ' - Dangerous' : ''}
                                </h5>
                                <Box>
                                    <div style={{ display: 'flex' }}>
                                        <Typography variant='caption' style={{ flex: 1 }}>
                                            | {new Date(min * 1000 * 60 * 60).toDateString()}
                                        </Typography>
                                        <Typography variant='caption' style={{ flex: 1, textAlign: 'right' }}>
                                            {new Date(max * 1000 * 60 * 60).toDateString()} |
                                        </Typography>
                                    </div>
                                    {r.map((r2, i) => {
                                        const from = r2.offer.validFrom ? new Date(r2.offer.validFrom).getTime() / 1000 / 60 / 60 : 0;
                                        const to = r2.offer.validUntil ? new Date(r2.offer.validUntil).getTime() / 1000 / 60 / 60 : 1139566;
                                        return (
                                            <div key={r2.id} style={{ textAlign: 'center', marginLeft: `${parseInt((from - min) * 100 / (max - min))}%`, width: `${parseInt(((to - from) * 100) / (max - min))}%`, height: '10px', marginBottom: '2px', backgroundColor: BACKGROUNDS[i % BACKGROUNDS.length], cursor: 'pointer' }} onClick={() => setOpenRate(r2)} >
                                            </div>
                                        );
                                    })}
                                </Box>
                            </div>
                        );
                    }).value()}
                </Box>
                <Dialog
                    fullScreen
                    open={!!openRate}
                    onClose={() => setOpenRate(null)}
                >   
                    <AppBar sx={{ position: 'relative' }}>
                        <Toolbar>
                            <IconButton
                                edge='start'
                                color='inherit'
                                onClick={() => setOpenRate(null)}
                                aria-label='close'
                            >
                                <CloseIcon />
                            </IconButton>
                            <div style={{ flex: 1 }} />
                            <Button onClick={() => { setDeleteCandidate(openRate.id); setOpenRate(null); }} sx={{ ml: 1, color: 'inherit', fontSize: '18px' }}><FontAwesomeIcon icon={faTrash} /></Button>
                        </Toolbar>
                    </AppBar>
                    {openRate && (
                        <Box sx={{ padding: '18px 48px' }}>
                            <RateCard organization={org} rate={openRate} exchangeRates={exchangeRates} standalone={true} />
                            <RatesDetails organization={org} rates={[openRate]} />
                        </Box>
                    )}
                </Dialog>
                <Dialog
                    open={!!deleteCandidate}
                    onClose={() => setDeleteCandidate(null)}
                >   
                    <DialogContent>
                        <DialogTitle>
                            Delete Overlapping Rate
                        </DialogTitle>
                        <DialogContentText>
                            Are you sure you want to delete this rate?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteCandidate(null)}>No</Button>
                        <Button onClick={() => onDeleteConfirm()}>Yes</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }

    function getCostValue(rate, costName) {
        let field = rate.offer.sections.map(s => s.offers[0].fields.find(f => f.title === costName)).find(f => f);
        if (!field) {
            field = rate.offer.sections.map(s => s.offers[0].fields.find(f => stringSimilarity.compareTwoStrings(f.title, costName) > 0.8)).find(f => f);
        }
        if (!field) return null;
        if (field.type === 'flat') return field.values.flat.value;
        if (field.type === 'string') return field.values.string.value;
        return field.values[rate.product.id].value;
    }

    const DAY = 1000 * 60 * 60 * 24;

    export default Rates;
    export { RatesMain }

    function matchProducts({ rates, products }) {
        const productConversionRates = rates.map(r => {
            const newProduct = products[r.transportationMethod]?.find(p => _.isEqual(_.pick(r.product, ['type', 'dangerous', 'volume', 'weight']), _.pick(p, ['type', 'dangerous', 'volume', 'weight'])));
            if (!newProduct) return r;
            return JSON.parse(JSON.stringify(r).replaceAll(r.product.id, newProduct.id));
        });
        return productConversionRates;
    }